/**
 * Elapsed efetivo de uma sessão de Foco — espelho CLIENT da regra do servidor.
 *
 * ## Por que este módulo existe
 *
 * Antes da pausa, o client derivava o tempo decorrido de `startedAt` em CINCO
 * lugares independentes dentro de `useFocusTimer`: o mostrador (`syncRemaining`),
 * o tick de 1s, o `computeRemainingFromSession` do pomodoro, o `dueMs` que
 * agenda a conclusão automática e o `openForSec` que detecta sessão fantasma.
 * Enquanto "decorrido" era `now − startedAt`, cinco cópias da mesma subtração
 * eram inofensivas.
 *
 * Com a pausa deixaram de ser: cada cópia esquecida vira um defeito diferente e
 * nenhum deles quebra teste de tipo. O mais grave é o `dueMs` — sem desconto,
 * **um pomodoro pausado completa sozinho enquanto está pausado**, e a pessoa
 * volta encontrando a sessão encerrada. É a doença do BUG-81/BUG-87 (duas
 * fontes da mesma regra divergindo) aplicada a tempo, e a cura é a mesma:
 * uma função, um dono.
 *
 * ## Relação com o servidor
 *
 * A autoridade é `computeEffectiveElapsedSec` em
 * `functions/src/shared/focusReward.ts` — é ele que decide o que a sessão VALE.
 * Este espelho existe porque o mostrador precisa responder a cada segundo sem
 * round-trip. A paridade das duas implementações é travada por teste
 * (`tests/components/focusElapsedMirror.test.ts`), no mesmo padrão de
 * `focusEconomy.ts` ↔ `focusReward.ts`.
 *
 * ⚠️ Divergir daqui não produz erro visível: produz um mostrador que discorda
 * da recompensa, que é exatamente a classe de defeito que o estudo de economia
 * (§1.5) catalogou como promessa×realidade.
 */

/** Espelho de `MAX_FOCUS_PAUSE_SEC` (functions/src/shared/constants.ts). */
export const MAX_FOCUS_PAUSE_SEC = 30 * 60;

/**
 * Espelho de `MAX_FOCUS_SESSION_ELAPSED_SEC` (functions/src/shared/constants.ts)
 * — mudou lá, mude aqui. Acima disto a sessão é fantasma: o cancelamento
 * otimista não chegou ao servidor, ou o app morreu com ela aberta.
 *
 * Morava em `focusService.ts` e veio pra cá em 21/08 (o `staleDate` da Live
 * Activity precisa dele, e este é o módulo puro — sem firebase no grafo).
 * `focusService` re-exporta; continua havendo UMA cópia no client.
 */
export const MAX_FOCUS_SESSION_ELAPSED_SEC = 4 * 60 * 60;

/**
 * Janela em que uma sessão VENCIDA ainda pode ser coletada (03/09).
 *
 * Não confundir com `MAX_FOCUS_SESSION_ELAPSED_SEC`, que mede outra coisa: lá a
 * pergunta é *"há quanto tempo esta sessão está ABERTA?"* e serve para achar
 * sessão esquecida rodando. Aqui a pergunta é *"há quanto tempo ela está
 * ESPERANDO ser coletada?"*, e a diferença não é semântica — era um bug.
 *
 * Um pomodoro de 25 min que venceu e foi reaberto 5 h depois caía em
 * `openForSec > 4 h`, era classificado como fantasma e **cancelado**: moedas,
 * XP, ofensiva e páginas, tudo destruído sem uma linha na tela. Mas ele tem
 * duração conhecida e terminou sozinho — não é uma sessão esquecida em aberto,
 * é uma sessão pronta para coletar.
 *
 * 12 h porque cobre o ciclo que a leitura tem de verdade — focar à noite e
 * abrir o app de manhã — sem deixar coletar sessão de dias atrás, cujo crédito
 * cairia no `dayKey` de HOJE e mentiria na trilha e na ofensiva. É um dial: o
 * custo de aumentar é crédito caindo mais longe do dia real da leitura.
 */
export const MAX_COLLECT_WAIT_SEC = 12 * 60 * 60;

/** Forma mínima de que a fórmula precisa — os 3 campos de pausa da sessão. */
export interface FocusPauseState {
  startedAtMs: number;
  /** `null` = não está pausada agora. */
  pausedAtMs: number | null;
  /** Acumulado das pausas JÁ RETOMADAS. Sessão pré-feature = 0. */
  pausedTotalSec: number;
}

function safeNonNegative(value: number | undefined): number {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : 0;
}

/**
 * `(pausedAt ?? now) − startedAt − pausedTotal`, em segundos.
 *
 * Congelar quando pausada não é um clamp — é a estrutura da conta: parado o
 * relógio em `pausedAt`, não importa há quanto tempo a sessão está assim. É por
 * isso que uma pausa esquecida não rende um segundo, e por isso que o teto de
 * 30 min governa só a RETOMADA, nunca este cálculo.
 */
export function computeEffectiveElapsedSec(state: FocusPauseState, nowMs: number): number {
  const freezeMs = state.pausedAtMs ?? nowMs;
  const rawSec = Math.floor((freezeMs - state.startedAtMs) / 1000);
  return Math.max(0, rawSec - safeNonNegative(state.pausedTotalSec));
}

/** A pausa vigente passou do teto de retomada. */
export function isPauseExpired(
  pausedAtMs: number,
  nowMs: number,
  maxPauseSec: number = MAX_FOCUS_PAUSE_SEC,
): boolean {
  return Math.floor((nowMs - pausedAtMs) / 1000) > maxPauseSec;
}

/**
 * Instante em que um pomodoro deve concluir, dado o estado de pausa.
 *
 * `null` enquanto a sessão está PAUSADA — e esse null é o ponto: sem ele o
 * agendamento continuaria mirando `startedAt + target`, e o alvo venceria com a
 * sessão congelada. O relógio de conclusão só existe enquanto o relógio da
 * sessão anda.
 *
 * Retomada, a data de vencimento é empurrada para frente por todo o tempo já
 * pausado, que é o que faz "25 min de foco" continuar significando 25 minutos
 * de foco, e não 25 minutos de parede.
 */
export function computeDueMs(
  state: FocusPauseState,
  targetDurationSec: number,
): number | null {
  if (targetDurationSec <= 0) return null;
  if (state.pausedAtMs != null) return null;
  return (
    state.startedAtMs + (targetDurationSec + safeNonNegative(state.pausedTotalSec)) * 1000
  );
}

/**
 * A forma mínima que as perguntas abaixo fazem de uma sessão. Deliberadamente
 * ESTRUTURAL e não o tipo `FocusSession`: este módulo é puro e não pode
 * arrastar `firebase/firestore` (o `Timestamp`) para o grafo — é o que deixa a
 * Live Activity e os testes o carregarem sem mockar o SDK.
 */
export interface FocusSessionTiming {
  targetDurationSec: number;
  startedAtMs: number;
  pausedAtMs: number | null;
  pausedTotalSec: number;
}

/** O estado de pausa de uma sessão, na forma que a fórmula consome. */
export function pauseStateOf(session: FocusSessionTiming): FocusPauseState {
  return {
    startedAtMs: session.startedAtMs,
    pausedAtMs: session.pausedAtMs,
    pausedTotalSec: session.pausedTotalSec,
  };
}

/**
 * A sessão tem alvo de tempo e ele JÁ VENCEU?
 *
 * Só isto separa "esperando ser coletada" de "esquecida rodando", e é a
 * pergunta que o provider (para abrir expandido) e o timer (para não descartar)
 * precisam fazer igual. Sessão pausada devolve `false` por construção
 * (`computeDueMs` não vence sessão congelada) e sessão livre também — ela não
 * tem alvo, então nunca "vence": é o caso legítimo do fantasma.
 */
export function hasTargetElapsed(session: FocusSessionTiming, nowMs: number): boolean {
  const dueMs = computeDueMs(pauseStateOf(session), session.targetDurationSec);
  return dueMs != null && nowMs >= dueMs;
}

/**
 * Há quantos segundos a sessão vencida espera coleta. `null` quando ela não
 * venceu — e a ausência é o ponto: quem chama tem de tratar "não venceu" como
 * um caso à parte, não como espera zero.
 */
export function waitingToCollectSec(
  session: FocusSessionTiming,
  nowMs: number,
): number | null {
  const dueMs = computeDueMs(pauseStateOf(session), session.targetDurationSec);
  if (dueMs == null || nowMs < dueMs) return null;
  return Math.floor((nowMs - dueMs) / 1000);
}

/**
 * A sessão vencida ainda vale a pena coletar?
 *
 * É o guard que substitui o descarte por `openForSec` no caminho de sessão com
 * alvo. Fora da janela ela volta a ser fantasma — o crédito cairia num dia que
 * não é o da leitura.
 */
export function isCollectable(session: FocusSessionTiming, nowMs: number): boolean {
  const waiting = waitingToCollectSec(session, nowMs);
  return waiting != null && waiting <= MAX_COLLECT_WAIT_SEC;
}

/** Para onde a sessão RECUPERADA deve ir quando o app reabre. */
export type FocusRecoveredRoute = "wrap_up" | "running";

/**
 * O roteamento final da recuperação — a decisão que estava escrita como uma
 * condição solta dentro do `recover` (BUG-204/F9).
 *
 * ── O ramo que faltava ──────────────────────────────────────────────────────
 *
 * Sessão **pausada além do teto de retomada** voltava como `running`. O
 * mostrador congelado aparecia com um botão "Retomar" que o servidor ia
 * RECUSAR: `resumeFocusSession` devolve `expired: true` e manda para o wrap-up
 * (invariante 16). Ou seja, a informação já existia dos dois lados — só não
 * chegava a quem abriu o app pelo ícone em vez do card da Ilha.
 *
 * É a quarta saída da recuperação que não levava ao wrap-up, e por isso mora
 * no BUG-204 em vez de virar bug próprio: mesma função, mesma superfície,
 * mesma classe.
 *
 * ⚠️ A pessoa NÃO perde o que leu, e é o que torna este ramo seguro: o elapsed
 * congelou em `pausedAt` (`computeEffectiveElapsedSec`), então o wrap-up abre
 * com o tempo real de foco. O servidor também não precisa ser avisado — no
 * ramo `expired` ele não escreve nada, então ir direto ao wrap-up produz
 * exatamente o mesmo estado que tocar "Retomar" produziria.
 *
 * ── Por que os dois ramos de wrap-up convivem ───────────────────────────────
 *
 * Pausa expirada e alvo vencido são perguntas diferentes, e nenhuma implica a
 * outra: uma sessão pausada aos 5 min de um pomodoro de 25 tem alvo em aberto
 * e retomada impossível; uma pausada no minuto 25 tem as duas. Por isso a
 * ordem entre eles não muda resultado nenhum — o que muda é existir o
 * primeiro, sem o qual o caso "pausou cedo e esqueceu" cai em `running`.
 *
 * `running` é o padrão de propósito: sem timing que prove o contrário, a
 * sessão continua sendo uma sessão.
 */
export function resolveRecoveredRoute(
  session: FocusSessionTiming,
  nowMs: number,
): FocusRecoveredRoute {
  if (session.pausedAtMs != null && isPauseExpired(session.pausedAtMs, nowMs)) {
    return "wrap_up";
  }
  // Sessão livre e por páginas não têm alvo cronometrado — elas só terminam
  // por gesto, e é por isso que a pergunta é guardada por `> 0` em vez de
  // comparar contra um alvo zerado (que venceria SEMPRE).
  if (session.targetDurationSec > 0) {
    const elapsed = computeEffectiveElapsedSec(pauseStateOf(session), nowMs);
    if (elapsed >= session.targetDurationSec) return "wrap_up";
  }
  return "running";
}

/**
 * Qual é A sessão quando a recuperação encontra mais de uma `running` — a mais
 * NOVA (BUG-231). Espelho de `pickNewestRunning` (servidor): o start e a
 * recuperação precisam apontar para o MESMO doc. Sem `startedAt` legível vai
 * por último; se só houver dessas, a primeira.
 */
export function pickNewestByStart<T extends { startedAtMs: number | null }>(
  items: readonly T[],
): T | null {
  if (items.length === 0) return null;
  let best: T | null = null;
  for (const it of items) {
    const ms =
      typeof it.startedAtMs === "number" && Number.isFinite(it.startedAtMs) ? it.startedAtMs : null;
    if (ms == null) continue;
    if (best == null || ms > (best.startedAtMs as number)) best = it;
  }
  return best ?? items[0];
}

/**
 * Folga do start otimista: uma sessão devolvida pelo servidor com `startedAt`
 * dentro desta janela é a NOSSA (toque duplo, retry), não uma órfã. Morava
 * como literal no `useFocusTimer`; subiu para cá porque a decisão que a usa é
 * pura (`resolveStartAckRoute`).
 */
export const START_ACK_GRACE_MS = 30_000;

/** O que o client faz com a sessão que `startFocusSession` devolveu. */
export type StartAckRoute =
  /** É a sessão que acabamos de pedir (ou um retry dela): adota o id. */
  | "adopt"
  /**
   * É uma sessão ANTIGA cujo alvo já venceu e ainda está na janela de coleta:
   * há crédito pendente. Adota E leva ao wrap-up — nunca cancela.
   */
  | "collect"
  /** É uma órfã sem crédito a preservar: cancela e pede outra (BUG-68). */
  | "supersede";

/**
 * Sessão devolvida pelo start que NÃO é a pedida (BUG-226).
 *
 * O servidor devolve a `running` existente e ignora o pedido novo; o client,
 * ao ver `startedAt` fora da folga, cancelava e recriava (BUG-68) — inclusive
 * quando a existente era um pomodoro VENCIDO esperando coleta, que é
 * exatamente o caso que `resolveStartConflict` (servidor) devolve de propósito
 * para não apagar o crédito. As duas pontas discordavam, e quem pagava era a
 * pessoa: moedas, XP, ofensiva e páginas de uma sessão terminada sumiam sem
 * uma linha na tela. Como chegar lá: recuperação que esgotou as tentativas,
 * segundo aparelho, ou toque em "Iniciar" antes de a leitura do boot resolver.
 *
 * A régua é a MESMA da recuperação (`isCollectable`): pausada e livre nunca
 * vencem, então continuam sendo supersedidas — é a decisão registrada do
 * BUG-68 para "quem toca Iniciar está pedindo uma sessão nova".
 */
export function resolveStartAckRoute(
  ack: FocusSessionTiming,
  localStartMs: number,
  nowMs: number,
): StartAckRoute {
  if (ack.startedAtMs >= localStartMs - START_ACK_GRACE_MS) return "adopt";
  if (isCollectable(ack, nowMs)) return "collect";
  return "supersede";
}

/**
 * Como nomear o instante de uma sessão que ficou pendente — "hoje às 22h05",
 * "ontem às 22h05".
 *
 * Existe porque o wrap-up recuperado abre horas depois do fato, e o cabeçalho
 * dele fala no presente ("Sessão finalizada!"). Sem dizer QUANDO, a pessoa lê
 * como se uma sessão tivesse acabado de terminar e declara páginas de uma
 * leitura que não lembra — ou pior, de outra.
 *
 * A janela de coleta é de 12 h, então só existem dois dias possíveis e nenhum
 * caso precisa de data por extenso. Fora deles a função devolve `null`, e quem
 * chama volta à copy de sempre: um rótulo errado é pior que rótulo nenhum.
 */
export function describeSessionMoment(atMs: number, nowMs: number): string | null {
  if (!Number.isFinite(atMs) || atMs > nowMs) return null;
  const at = new Date(atMs);
  const now = new Date(nowMs);
  const dias = Math.round(
    (new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() -
      new Date(at.getFullYear(), at.getMonth(), at.getDate()).getTime()) /
      86_400_000,
  );
  const dia = dias === 0 ? "hoje" : dias === 1 ? "ontem" : null;
  if (dia == null) return null;
  const hora = `${at.getHours()}h${String(at.getMinutes()).padStart(2, "0")}`;
  return `${dia} às ${hora}`;
}
